import React, { useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import { get } from 'lodash';
import cx from 'classnames';

import { CollapseIcon, ExpandIcon, CrossIcon } from '@/assets/icons';
import { getUserRoleNameByID } from '@/utils/helper';
import { PageLoader } from '@/views/component/Loader';
import {
	sendCustomAnalyticsEvent,
	sendClickEvent,
} from '@/utils/AnalyticsUtils';
import {
	RN_OPEN_CHATBOT,
	TEST_IDS,
	MERCHANT_CHATBOT_KEYS,
	CHATBOT_CLOSE_CLICKED,
	CHATBOT_EXPAND_ICON_CLICKED,
	CHATBOT_MINIMIZE_ICON_CLICKED,
} from '@/constants';
import { RootState } from '@/redux/ducks';

import styles from './ChatBot.module.scss';

interface IChatBotProps {
	merchantProfile: RootState['merchantProfile'];
	chatBotToken: string;
	close: () => void;
	src: MERCHANT_CHATBOT_KEYS;
	contextualBotData?: IContextualBotData;
}
interface IContextualBotData {
	txnDate?: string | number;
	txnAmount?: number | string;
	txnId?: string;
}

const ChatBot = ({
	merchantProfile,
	chatBotToken,
	contextualBotData,
	src,
	close,
}: IChatBotProps) => {
	const { user } = useSelector((state: RootState) => state.userAuth);
	const userDetails = useSelector((state: RootState) => state.userProfile);
	const roleName = getUserRoleNameByID(
		userDetails?.userRolesList?.data,
		user?.roleId,
	)?.name;

	const [isWindowMaximized, setIsWindowMaximized] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const merchantDisplayName = useSelector(() =>
		get(merchantProfile, 'merchantDetails.displayName', ''),
	);
	const merchantHashId = get(
		merchantProfile,
		'merchantDetails.merchantHashId',
		'',
	);
	const faqIFrame = useRef(null);

	const handleClose = () => {
		sendClickEvent({
			name: CHATBOT_CLOSE_CLICKED,
			value: src,
		});
		close();
	};

	const toggleChatbotFullScreen = () => {
		sendClickEvent({
			name: isWindowMaximized
				? CHATBOT_MINIMIZE_ICON_CLICKED
				: CHATBOT_EXPAND_ICON_CLICKED,
			value: src,
		});
		setIsWindowMaximized(!isWindowMaximized);
	};
	const onFrameLoadHandler = () => {
		setIsLoading(false);
		sendCustomAnalyticsEvent('custom', {
			additionalData: {
				MID: get(merchantProfile, 'merchantDetails.merchantHashId', ''),
				src,
				name: get(merchantProfile, 'merchantDetails.displayName', ''),
				txnId: contextualBotData?.txnId,
				role: roleName,
			},
			name: RN_OPEN_CHATBOT,
		});
	};
	const getChatBotUrl = (contextualBotData?: IContextualBotData) => {
		const baseUrl = '/chatBot.html';
		const staticParams = `xtrnlTcktId=${encodeURIComponent(src)}&MID=${merchantHashId}&src=${encodeURIComponent(src)}&token=${chatBotToken}&name=${encodeURIComponent(merchantDisplayName)}&webidentifier=yes&role=${roleName}`;

		let dynamicParams = '';
		if (contextualBotData) {
			Object.entries(contextualBotData).forEach(([key, value]) => {
				if (value) {
					dynamicParams += `&${key}=${value}`;
				}
			});
		}

		const queryString = dynamicParams
			? `${staticParams}${dynamicParams}`
			: staticParams;

		return `${baseUrl}?${queryString}`;
	};
	return (
		<div
			data-id={TEST_IDS.CHATBOT_CONTAINER}
			className={cx(styles.iframeWrapper, {
				[styles.iframeWrapperMaximized]: isWindowMaximized,
			})}
		>
			{!isLoading ? (
				<>
					<CrossIcon
						className={styles.chatbotCloseIcon}
						onClick={handleClose}
						data-id={TEST_IDS.CLOSE_CHAT_BOT_BUTTON}
					/>
					{isWindowMaximized ? (
						<CollapseIcon
							className={styles.chatbotMaximizeIcon}
							onClick={toggleChatbotFullScreen}
							data-id={TEST_IDS.COLLAPSE_CHAT_BOT_BUTTON}
						/>
					) : (
						<ExpandIcon
							className={styles.chatbotMaximizeIcon}
							onClick={toggleChatbotFullScreen}
							data-id={TEST_IDS.EXPAND_CHAT_BOT_BUTTON}
						/>
					)}
				</>
			) : (
				<div
					data-id={TEST_IDS.LOADER_CHAT_BOT_BUTTON}
					className={styles.loader}
				>
					<PageLoader size="small" />
				</div>
			)}
			<iframe
				data-id={TEST_IDS.CHAT_BOT_IFRAME}
				name="chatBotFreshDesk"
				className={styles.iframe}
				src={getChatBotUrl(contextualBotData)}
				ref={faqIFrame}
				title="FAQ"
				onLoad={onFrameLoadHandler}
			/>
		</div>
	);
};

export default ChatBot;
